import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { formatTime } from '../../../musicVideoEditor/timeline/timelineGeometry';
import { useDirectedVideoContext } from '../../DirectedVideoContext';
import { resultMessages as messages } from '../../messages/resultMessages';
import { EFFECT_FAMILY_NAMES } from '../director/familyMessages';
import type { DirectedResultFacts } from './resultRules';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the result; each part uses its own slice of it */
import styles from './Result.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface Fact {
  key: string;
  label: string;
  value: string;
  sub: string;
  isBack?: boolean;
}

/**
 * The details of the finished video (design directed-video k1, k2): the shots and the new clips they
 * come from, the effects, the length and the credits charged. A label sits over its value, so no
 * wide gap opens between them (owner report 2026-10-18).
 */
const ResultFactsComponent: React.FC<{ facts: DirectedResultFacts }> = ({ facts }) => {
  const intl = useIntl();
  const { video, song } = useDirectedVideoContext();
  const length = video.asset?.durationSec ?? song.durationSec ?? 0;
  const height = video.asset?.height ?? null;
  const familyNames = useMemo(
    () => facts.families.map((family) => intl.formatMessage(EFFECT_FAMILY_NAMES[family]).toLowerCase()),
    [facts.families, intl]
  );

  const items: Fact[] = [
    {
      key: 'shots',
      label: intl.formatMessage(messages.shots),
      value: intl.formatNumber(facts.shots),
      sub: intl.formatMessage(messages.shotsFrom, { count: facts.clipsMade }),
    },
    {
      key: 'effects',
      label: intl.formatMessage(messages.effects),
      value: intl.formatNumber(facts.effects),
      sub: familyNames.length > 0 ? intl.formatList(familyNames) : intl.formatMessage(messages.noEffects),
    },
    {
      key: 'length',
      label: intl.formatMessage(messages.length),
      value: formatTime(length),
      sub:
        height === null
          ? intl.formatMessage(messages.lengthSub)
          : intl.formatMessage(messages.lengthSubSize, { height }),
    },
    {
      key: 'charged',
      label: intl.formatMessage(messages.charged),
      value: intl.formatMessage(messages.creditsValue, { count: facts.chargedCredits }),
      sub:
        facts.backCredits > 0
          ? intl.formatMessage(messages.chargedBack, {
              credits: intl.formatMessage(messages.creditsValue, { count: facts.backCredits }),
              count: facts.clipsFailed,
            })
          : intl.formatMessage(messages.chargedSubSeconds, { count: facts.clipsMade, seconds: facts.madeFootageSec }),
      isBack: facts.backCredits > 0,
    },
  ];

  return (
    <dl
      className={styles.facts}
      aria-label={intl.formatMessage(messages.factsLabel)}
      data-testid="directed-result-facts"
    >
      {items.map((item) => (
        <div key={item.key} className={styles.fact} data-fact={item.key}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
          <dd className={clsx(styles.factSub, item.isBack === true && styles.back)}>{item.sub}</dd>
        </div>
      ))}
    </dl>
  );
};

export const ResultFacts = TypedMemo(ResultFactsComponent);
